import { useMemo } from 'react';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { taskFiltersToRequest } from 'components/tasks/form/upstream';
import { injectStatusFilter, injectUnarchivedFilter } from 'components/tasks/utils/upstream';
import { useInfiniteTasks } from 'components/tasks/utils/useInfiniteTasks';
import {
    taskFilterValuesSelector,
    useTasksFilterStore,
} from 'components/tasks/utils/useTasksFilterStore';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';

const useTasks = () => {
    const { query, ...restFilters } = useTasksFilterStore(taskFilterValuesSelector);

    const { filters, filterGroups } = useFiltersByKey('tasks');
    const hasArchivedFilter = filterGroups.find(f =>
        f.filters.find(f => f.serverKey === 'archived'),
    );
    const requestFilters = hasArchivedFilter ? filters : injectUnarchivedFilter(filters);

    const debouncedQuery = useDebounce(query);

    const [, params] = useIsPending(
        taskFiltersToRequest({
            query: debouncedQuery.trim(),
            ...restFilters,
        }),
        500,
    );

    const {
        ref: todoInfiniteRef,
        data: { tasks: todoTasks, meta: todoMeta },
    } = useInfiniteTasks({
        ...params,
        filters: injectStatusFilter(['todo'], requestFilters),
    });

    const {
        ref: inProgressInfiniteRef,
        data: { tasks: inProgressTasks, meta: inProgressMeta },
    } = useInfiniteTasks({
        ...params,
        filters: injectStatusFilter(['in_progress'], requestFilters),
    });

    const {
        ref: completeInfiniteRef,
        data: { tasks: completeTasks, meta: completeMeta },
    } = useInfiniteTasks({
        ...params,
        filters: injectStatusFilter(['complete'], requestFilters),
    });

    const tasks = useMemo(
        () => [...todoTasks, ...inProgressTasks, ...completeTasks],
        [todoTasks, inProgressTasks, completeTasks],
    );

    return {
        tasks,
        meta: {
            todo: todoMeta,
            in_progress: inProgressMeta,
            complete: completeMeta,
        },
        refs: {
            todo: todoInfiniteRef,
            in_progress: inProgressInfiniteRef,
            complete: completeInfiniteRef,
        },
    };
};

export { useTasks };
